import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { createTheme, alpha } from '@mui/material/styles';
import Login from './components/Login';
import AdminLayout from './components/AdminLayout';
import { DatosFirestoreProvider } from './contexts/DatosFirestoreContext';

// 📦 Cada pantalla en su propio archivo de JavaScript, que se pide al entrar en
// ella. Antes todo iba en uno solo de 2,6 MB (FormBuilder, FormResponses…) que
// se descargaba entero ya en el login. Login y AdminLayout se quedan dentro:
// son lo primero que se pinta y no tiene sentido esperar a otra descarga.
// La espera de cada pantalla la cubre el `Suspense` del `<Outlet>` en AdminLayout.
const Dashboard = lazy(() => import('./components/Dashboard'));
const FormBuilder = lazy(() => import('./components/FormBuilder'));
const FormsList = lazy(() => import('./components/FormsList'));
const UsersList = lazy(() => import('./components/UsersList'));
const GroupsManager = lazy(() => import('./components/GroupsManager'));
const FormResponses = lazy(() => import('./components/FormResponses'));
const Transferencias = lazy(() => import('./components/Transferencias'));
const DataList = lazy(() => import('./components/DataList'));
const PoliticaPrivacidad = lazy(() => import('./components/PoliticaPrivacidad'));
const Soporte = lazy(() => import('./components/Soporte'));

/** Lo que se ve mientras llega el JavaScript de una pantalla. */
const CargandoPantalla = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

// 🎨 Tema personalizado optimizado (claro/oscuro)
const buildTheme = (darkMode) => {
  const base = crearBase(darkMode);

  // 🎨 LOS TINTES: los fondos muy claros que marcan una zona —la caja de
  // "arrastra aquí un campo", la zona de la plantilla Word, un aviso—.
  //
  // No son colores nuevos: son `primary`, `success`, `warning`, `error` y
  // `secondary` rebajados. Existen porque estaban escritos a mano cuarenta
  // veces (#e3f2fd, #fff3e0, #e8f5e8, #f3e5f5…) y en modo oscuro seguían
  // siendo manchas pálidas: al calcularse sobre el color del tema, en oscuro
  // salen tintados del mismo tono pero sobre el fondo oscuro.
  //
  // Se usan por su nombre, como cualquier otra ficha de la paleta:
  // `sx={{ bgcolor: 'tinte.aviso' }}`.
  const velo = (color) => alpha(color, darkMode ? 0.16 : 0.10);

  return createTheme(base, {
    palette: {
      tinte: {
        primario: velo(base.palette.primary.main),
        exito: velo(base.palette.success.main),
        aviso: velo(base.palette.warning.main),
        error: velo(base.palette.error.main),
        secundario: velo(base.palette.secondary.main),
        // El neutro no tiene color: es el gris de fondo de cada modo
        neutro: base.palette.action.hover
      }
    }
  });
};

const crearBase = (darkMode) => createTheme({
  palette: {
    mode: darkMode ? 'dark' : 'light',
    // 🌙 En oscuro los dos colores se ACLARAN, y no es estética: son los que
    // pintan el texto y los iconos de `color="primary"` / `color="secondary"`
    // por todo el panel, y sobre el fondo #121212 el azul de siempre (#1976d2)
    // da 4,1:1 — por debajo del 4,5:1 que necesita un texto normal. #90caf9 da
    // 10,7:1 y #ce93d8, 7,8:1.
    //
    // 💜 El `secondary` es el MORADO de la APK (`md_theme_dark_secondary`,
    // `Theme.kt`), no el rosa #dc004e que hubo aquí: las dos caras del producto
    // tenían cada una el suyo porque la APK copió su paleta de un `src/theme.js`
    // que el panel no llegaba a usar. Ese archivo ya no existe; este es el único
    // tema, y ahora los dos dicen lo mismo.
    //
    // Lo que va ENCIMA de un fondo de color (la portada, la cabecera de la
    // tabla, los títulos de los diálogos) no puede usarlos: ahí el fondo tiene
    // que seguir siendo oscuro para que el texto blanco se lea, y de eso se
    // encarga `utils/degradadoCabecera.js`.
    primary: {
      main: darkMode ? '#90caf9' : '#1976d2',
    },
    secondary: {
      main: darkMode ? '#ce93d8' : '#9c27b0',
    },
    ...(darkMode && {
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
    }),
  },
  components: {
    // 🎨 Estilos mejorados sin props problemáticas
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Evita mayúsculas automáticas
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

function App() {
  // 🌙 Tema oscuro: se activa desde Configuración en AdminLayout
  // (evento 'ignia-theme-change') y se recuerda en localStorage
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ignia-dark-mode') === 'true');

  useEffect(() => {
    const handleThemeChange = (e) => setDarkMode(Boolean(e.detail));
    window.addEventListener('ignia-theme-change', handleThemeChange);
    return () => window.removeEventListener('ignia-theme-change', handleThemeChange);
  }, []);

  const theme = useMemo(() => buildTheme(darkMode), [darkMode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense fallback={<CargandoPantalla />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            {/* 📄 Política de privacidad: PÚBLICA y sin sesión. Es la URL que
                Google Play exige para la ficha de IGNIA, así que debe seguir
                accesible mientras la app esté publicada. `/privacy` es un alias
                por si el enlace se comparte en inglés. */}
            <Route path="/privacidad" element={<PoliticaPrivacidad />} />
            <Route path="/privacy" element={<Navigate to="/privacidad" replace />} />
            {/* 🛟 Soporte: PÚBLICA y sin sesión, como la de arriba. Apple exige
                esta URL en la ficha de la App Store y rechaza un enlace roto */}
            <Route path="/soporte" element={<Soporte />} />
            <Route path="/support" element={<Navigate to="/soporte" replace />} />
            {/* 🔥 El proveedor envuelve todo /admin: así la suscripción a
                respuestas y los catálogos se comparten entre pantallas en vez de
                volver a leerse en cada navegación */}
            <Route
              path="/admin"
              element={
                <DatosFirestoreProvider>
                  <AdminLayout />
                </DatosFirestoreProvider>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              {/* Aquí hubo un /admin/executive-dashboard. Se retiró la ruta porque
                  `ExecutiveDashboard.js` pintaba datos inventados
                  (`generateMockData`), y el 21/09/2026 se borraron también el
                  componente y su CSS: llevaban meses sin ruta y sin datos reales.
                  Están en el historial de git si alguien retoma la idea. */}
              <Route path="create-form" element={<FormBuilder mode="create" />} />
              <Route path="forms" element={<FormsList />} />
              <Route path="forms/:id/edit" element={<FormBuilder mode="edit" />} />
              <Route path="responses" element={<FormResponses />} />
              <Route path="transfers" element={<Transferencias />} />
              <Route path="groups" element={<GroupsManager />} />
              <Route path="data-lists" element={<DataList />} />
              <Route path="users" element={<UsersList />} />
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App; 